import React from "react";
import { expirationStatus, EXP_COLOR_TOKENS } from "@/lib/expirationStatus";

/**
 * RC10i · Shared expiration badge.
 *
 * Renders the status label + color produced by `expirationStatus(...)`.
 * Both color AND text are present — never color alone (a11y).
 *
 * Props:
 *   - value: ISO date | Date | null | undefined
 *   - accessState: "restricted" (server-redacted) | undefined
 *   - testid: data-testid prefix (e.g. "cdl-exp" → `cdl-exp-badge-<status>`)
 *   - showDate: if true, prefixes the formatted date (`MMM D, YYYY · <badge>`).
 *               Default true on desktop; mobile stacks the date above the
 *               badge, so pass `false` and render the date yourself.
 */
export const ExpirationBadge = ({ value, accessState, testid, showDate = true, className = "" }) => {
  const s = expirationStatus(value, { accessState });
  const tokens = EXP_COLOR_TOKENS[s.color] || EXP_COLOR_TOKENS.gray;
  return (
    <span
      data-testid={testid ? `${testid}-badge-${s.status}` : undefined}
      data-exp-status={s.status}
      data-exp-color={s.color}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap ${className}`}
    >
      {showDate && s.displayDate && (
        <span data-testid={testid ? `${testid}-date` : undefined} className="text-[12.5px] text-[color:var(--fg-primary)]">
          {s.displayDate}
        </span>
      )}
      {showDate && s.displayDate && <span aria-hidden="true" className="text-[color:var(--fg-muted)]">·</span>}
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10.5px] font-semibold tracking-wide uppercase"
        style={{
          background: tokens.bg,
          border: `1px solid ${tokens.border}`,
          color: tokens.fg,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: 999,
            background: tokens.fg,
          }}
        />
        {s.label}
      </span>
    </span>
  );
};

/**
 * Mobile variant — stacks date above badge. Used in roster cards.
 */
export const ExpirationRow = ({ label, value, accessState, testid }) => {
  const s = expirationStatus(value, { accessState });
  const tokens = EXP_COLOR_TOKENS[s.color] || EXP_COLOR_TOKENS.gray;
  return (
    <div
      data-testid={testid ? `${testid}-row` : undefined}
      className="flex items-center justify-between gap-2 py-1"
    >
      <span className="text-[11px] uppercase tracking-widest text-[color:var(--fg-muted)]">
        {label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
        {s.displayDate && (
          <span className="text-[12.5px] text-[color:var(--fg-primary)]">
            {s.displayDate}
          </span>
        )}
        <span
          data-testid={testid ? `${testid}-badge-${s.status}` : undefined}
          data-exp-status={s.status}
          data-exp-color={s.color}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-semibold tracking-wide uppercase shrink-0"
          style={{
            background: tokens.bg,
            border: `1px solid ${tokens.border}`,
            color: tokens.fg,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 5,
              height: 5,
              borderRadius: 999,
              background: tokens.fg,
            }}
          />
          {s.label}
        </span>
      </div>
    </div>
  );
};
